import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/shared/components/ui/tabs';
import { AnalyticsFilterBar } from './AnalyticsFilterBar';
import { RefreshIndicator } from './RefreshIndicator';
import { StaleDataBanner } from './StaleDataBanner';
import { OverviewTab } from './tabs/OverviewTab';
import { NetworkSupplyTab } from './tabs/NetworkSupplyTab';
import { FinancialPartnersTab } from './tabs/FinancialPartnersTab';
import { useAnalyticsFilters } from '../../hooks/useAnalyticsFilters';
import { useAnalyticsRefresh } from '../../hooks/useAnalyticsRefresh';
import { analyticsApi } from '../../services/analytics-api.service';

type TabValue = 'overview' | 'network-supply' | 'financial-partners';

export const AnalyticsDashboard = () => {
  const [activeTab, setActiveTab] = useState<TabValue>('overview');

  const { data: partnerData } = useQuery({
    queryKey: ['analytics', 'partner-companies'],
    queryFn: analyticsApi.getPartnerCompanies,
    staleTime: 10 * 60 * 1000,
  });

  const filters = useAnalyticsFilters();
  const refresh = useAnalyticsRefresh();

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-gray-200 bg-white px-4 py-4 lg:px-6 lg:py-6">
        <h1 className="text-xl lg:text-2xl font-bold text-gray-900">
          Ecosystem Analytics
        </h1>
        <p className="text-sm lg:text-base text-gray-600 mt-1">
          Platform-wide analytics and ecosystem health
        </p>
      </div>

      <AnalyticsFilterBar
        dateRange={filters.dateRange}
        onDateRangeChange={filters.setDateRange}
        partnerCompanies={partnerData?.companies ?? []}
        selectedPartnerCompanyId={filters.partnerCompanyId}
        onPartnerCompanyChange={filters.setPartnerCompanyId}
        excludeInternal={filters.excludeInternal}
        onExcludeInternalChange={filters.setExcludeInternal}
        onRefresh={refresh.triggerRefresh}
        isRefreshing={refresh.isRefreshing}
        canRefresh={refresh.canRefresh}
        cooldownRemainingMs={refresh.cooldownRemainingMs}
      />

      <div className="px-4 lg:px-6 py-4 space-y-4">
        <div className="flex items-center justify-between">
          <RefreshIndicator
            lastRefreshed={refresh.oldestRefresh}
            isRefreshing={refresh.isRefreshing}
          />
        </div>

        <StaleDataBanner isStale={refresh.isStale} />

        <Tabs
          defaultValue="overview"
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as TabValue)}
        >
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="network-supply">
              Network &amp; Supply
            </TabsTrigger>
            <TabsTrigger value="financial-partners">
              Financial Partners
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <OverviewTab
              queryParams={filters.queryParams}
              isActive={activeTab === 'overview'}
            />
          </TabsContent>

          <TabsContent value="network-supply">
            <NetworkSupplyTab
              queryParams={filters.queryParams}
              isActive={activeTab === 'network-supply'}
            />
          </TabsContent>

          <TabsContent value="financial-partners">
            <FinancialPartnersTab
              queryParams={filters.queryParams}
              isActive={activeTab === 'financial-partners'}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
